import { useForm, Controller } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChannelsService } from '../../../../services/channels.service';
import { AlertService } from '../../../../services/alert.service';
import { ProvidersService } from '../../../../services/providers.service';
import useInputValidation from '../../../../hooks/use-input-validation.hook';
import Select from 'react-select';
import Input from '../../../../components/forms/input.component';
import Button from '../../../../components/forms/button.component';
import Title from '../../../../components/forms/titles.component';
import InvalidFeedback from '../../../../components/forms/invalid-feedback.component';
import useModal from '../../../../hooks/use-close-modal.hook';
import LogData from '../../../../components/logs/log-data.component';
import Icon from '../../../../components/forms/icon.component';

interface AddAccountProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload: any;
}

function AddAccount({ show, onRequestClose, payload }: AddAccountProps) {
  const { t } = useTranslation();
  const [providers, setProviders] = useState([]);
  const modalRef = useRef<HTMLDivElement>(null);
  const { tenDigitsRegex, numericRegex, alphanumericRegex, handleAlphanumericInput } =
    useInputValidation();

  useModal(modalRef, show);
  const defaultValues = {
    _id: '',
    provider: '',
    name: '',
    settings: {
      phone_number: '',
      phone_number_id: '',
      waba_id: '',
      app_id: '',
      token: ''
    }
  };

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const registerAccount = async (data: any, buttonId: string) => {
    try {
      if (payload != null) {
        const confirmed = await AlertService.question(
          t('general.atention'),
          `${t('channels.wa.q1')}, ${payload?.name}.`,
          null as any
        );
        if (!confirmed) {
          return;
        }
        data.settings.phone_number = '52' + data.settings.phone_number;
        await ChannelsService.updateAccountWhatsApp(data);
        AlertService.toastSuccess(t('channels.wa.success-add'));
      } else {
        const confirmed = await AlertService.question(
          t('general.atention'),
          t('channels.wa.q2'),
          null as any
        );
        if (!confirmed) {
          return;
        }
        delete data._id;
        data.settings.phone_number = '52' + data.settings.phone_number;
        await ChannelsService.saveAccountWhatsApp(data);
        AlertService.toastSuccess(t('channels.wa.success-edit'));
      }
      LogData(data, buttonId, window.location.href);
      await onRequestClose(true);
      reset(defaultValues);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const resetForm = () => {
    onRequestClose(false);
    reset(defaultValues);
  };

  const getProviders = async () => {
    try {
      const data = await ProvidersService.getProviders();
      setProviders(
        data.map((item: any) => ({
          value: item._id,
          label: item.name
        }))
      );
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  useEffect(() => {
    getProviders();
  }, []);

  useEffect(() => {
    reset({
      _id: payload?._id ? payload._id : defaultValues._id,
      provider: payload?.provider ? payload.provider : defaultValues.provider,
      name: payload?.name ? payload.name : defaultValues.name,
      settings: {
        phone_number: payload?.settings?.phone_number
          ? payload.settings.phone_number.replace('52', '')
          : defaultValues.settings.phone_number,
        phone_number_id: payload?.settings?.phone_number_id
          ? payload.settings.phone_number_id
          : defaultValues.settings.phone_number_id,
        waba_id: payload?.settings?.waba_id
          ? payload.settings.waba_id
          : defaultValues.settings.waba_id,
        app_id: payload?.settings?.app_id ? payload.settings.app_id : defaultValues.settings.app_id,
        token: payload?.settings?.token ? payload.settings.token : defaultValues.settings.token
      }
    });
  }, [payload]);

  return (
    <div className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog mw-1000px">
        <div className="modal-content">
          <div className="modal-header justify-content-end border-0 pb-0">
            <div
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={() => resetForm()}
            >
              <Icon icon="ki-cross" size="fs-2x" />
            </div>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const buttonID = watch('_id') ? 'editAccount-7326478503' : 'addAccount-3394857490';
              handleSubmit((data) => registerAccount(data, buttonID))(e);
            }}
          >
            <div className="modal-body pt-0 pb-15 px-5 px-xl-20">
              <div className="mt-1 text-center">
                {watch('_id') ? (
                  <>
                    <h1>
                      {t('channels.wa.modal-title-edit')}{' '}
                      <small className="text-primary fw-semibold fs-2x ms-2">{payload?.name}</small>
                    </h1>
                  </>
                ) : (
                  <h1>{t('channels.wa.modal-title')}</h1>
                )}
                <div className="text-muted fw-semibold fs-5">
                  <span
                    dangerouslySetInnerHTML={{ __html: t('channels.rcs.modal-C-Subtitle') }}
                  ></span>
                </div>
                <div className="separator border-2 my-5"></div>
              </div>
              <div className="row my-10 px-10">
                <div className="col-lg-6 mb-10 mb-lg-0  h-100 p-10">
                  <Title
                    icon={'ki-notepad-edit'}
                    title={t('channels.wa.modal-data')}
                    subtitle={
                      watch('_id')
                        ? t('channels.wa.modal-data-sub-edit')
                        : t('channels.wa.modal-data-sub')
                    }
                  />

                  <div className="fv-row mb-7">
                    <Input
                      label={t('channels.wa.name')}
                      requiredIndicator="required"
                      placeholder={t('channels.wa.placecholder-name')}
                      errors={errors.name}
                      {...register('name', {
                        required: true,
                        setValueAs: (value) => value.trim()
                      })}
                    />
                  </div>

                  <div className="fv-row mb-7">
                    <label className="form-label required">{t('channels.wa.provider')}</label>
                    <Controller
                      control={control}
                      name="provider"
                      rules={{ required: true }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <>
                          <Select
                            options={providers}
                            noOptionsMessage={({ inputValue }) =>
                              inputValue
                                ? t('general.no-options', { inputValue })
                                : t('general.no-options')
                            }
                            className={
                              error ? 'form-select-custom is-invalid' : 'form-select-custom'
                            }
                            classNamePrefix="form-select-custom"
                            placeholder={t('channels.wa.placecholder-provider')}
                            onChange={(selectedOption: { value: string; label: string } | null) => {
                              onChange(selectedOption ? selectedOption.value : null);
                            }}
                            value={
                              providers.find(
                                (option: { value: string; label: string }) => option.value === value
                              ) || null
                            }
                          />
                          <InvalidFeedback error={error} />
                        </>
                      )}
                    />
                  </div>
                </div>
                <div className="col-lg-6 mb-10 mb-lg-0  h-100 p-10">
                  <Title
                    icon={'ki-setting-2'}
                    type={'info'}
                    title={t('channels.wa.modal-settings')}
                    subtitle={
                      watch('_id')
                        ? t('channels.wa.modal-settings-sub-edit')
                        : t('channels.wa.modal-settings-sub')
                    }
                  />

                  <div className="fv-row mb-7">
                    <Input
                      label={t('channels.wa.number')}
                      requiredIndicator="required"
                      placeholder={t('channels.wa.placecholder-number')}
                      errors={errors?.settings?.phone_number}
                      {...register('settings.phone_number', {
                        required: true,
                        setValueAs: (value) => value.trim(),
                        pattern: {
                          value: tenDigitsRegex,
                          message: t('channels.wa.validate-1')
                        }
                      })}
                    />
                  </div>

                  <div className="fv-row mb-7">
                    <Input
                      label={t('channels.wa.id-number')}
                      requiredIndicator="required"
                      placeholder={t('channels.wa.placecholder-IDnumber')}
                      errors={errors?.settings?.phone_number_id}
                      {...register('settings.phone_number_id', {
                        required: true,
                        setValueAs: (value) => value.trim(),
                        pattern: {
                          value: numericRegex,
                          message: t('channels.wa.validate-2')
                        }
                      })}
                    />
                  </div>

                  <div className="fv-row mb-7">
                    <Input
                      label={t('channels.wa.id-waba')}
                      requiredIndicator="required"
                      placeholder={t('channels.wa.placecholder-IDwaba')}
                      errors={errors?.settings?.waba_id}
                      {...register('settings.waba_id', {
                        required: true,
                        setValueAs: (value) => value.trim(),
                        pattern: {
                          value: numericRegex,
                          message: t('channels.wa.validate-2')
                        }
                      })}
                    />
                  </div>

                  <div className="fv-row mb-7">
                    <Input
                      label={t('channels.wa.id-app')}
                      requiredIndicator="required"
                      placeholder={t('channels.wa.placecholder-IDapp')}
                      errors={errors?.settings?.app_id}
                      {...register('settings.app_id', {
                        required: true,
                        setValueAs: (value) => value.trim(),
                        pattern: {
                          value: numericRegex,
                          message: t('channels.wa.validate-2')
                        }
                      })}
                    />
                  </div>

                  <div className="fv-row">
                    <Input
                      label={t('channels.wa.token')}
                      requiredIndicator="required"
                      placeholder={t('channels.wa.placecholder-token')}
                      onKeyDown={handleAlphanumericInput}
                      errors={errors?.settings?.token}
                      {...register('settings.token', {
                        required: true,
                        setValueAs: (value) => value.trim(),
                        pattern: {
                          value: alphanumericRegex,
                          message: t('channels.wa.validate-3')
                        }
                      })}
                    />
                  </div>
                </div>
              </div>
              <div className="d-flex flex-center flex-row-fluid pt-12">
                <button
                  type="button"
                  className="btn btn-light hover-scale"
                  onClick={() => resetForm()}
                >
                  {t('general.close')}
                </button>
                <Button
                  type="submit"
                  id="sendData"
                  className="btn btn-primary ms-3 hover-scale"
                  disabled={!isValid}
                  isLoading={isSubmitting}
                >
                  {t('general.save')} <Icon icon="ki-send" size="fs-1" ClassName="ms-2" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddAccount;
